// ./src/hooks/useGoogleMapsAutocomplete.tsƒ
import { useRef, useState, useEffect, useCallback } from "react";
import { useGoogleMapsContext } from "../GoogleMapsContext";
import { GeoCoords } from "./useGeolocation";
import { isValidCoordinate } from "@em/utils";

// ============================================
// Types
// ============================================

export interface AddressData {
  addressId: string;
  displayName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  postalCode: string | null;
  region: string | null;
  placeType?: string | null;
  /**
   * True when Google classifies this as a named place (a business, venue, POI)
   * rather than a plain street address. It is the ONLY reliable way to tell the
   * two apart: `displayName` is the business name for an establishment but the
   * street line for an address, and the two can't be told apart by comparing
   * strings (Google abbreviates inconsistently — displayName "160 Woodglen Way
   * SW" vs address1 "160 Woodglen Way Southwest").
   *
   * Absent (geocoded results, saved addresses) means "not known to be a place",
   * which is the safe default — see `formatPlaceLine`.
   */
  isEstablishment?: boolean | null;
}

export interface AddressPrediction {
  addressId: string;
  displayName: string;
  types: string[];
  distanceMeters?: number | null;
}

export interface PlacePrediction {
  placeId: string;
  name: string;
  address: string;
  types: string[];
  distanceMeters?: number | null;
}

export interface PlaceData {
  placeId: string;        // Google place ID (existing)
  savedPlaceId?: string;  // App's saved place ID (new, optional)
  name: string;
  address: AddressData;
  /** Business website, when the place has one. */
  website?: string | null;
  /** Contact phone (national format preferred, else international). */
  phone?: string | null;
  /** Deep link to the place on Google Maps. */
  googleMapsUri?: string | null;
}

export interface AddressSearchOptions {
  /** Session token for billing optimization */
  sessionToken?: any;
  /** Location bias for relevant results */
  locationBias?: GeoCoords | null;
  /** Radius in meters for location bias (default: 50km) */
  radiusMeters?: number;
}

export interface PlaceSearchOptions {
  /** Session token for billing optimization */
  sessionToken?: any;
  /** Location bias for relevant results */
  locationBias?: GeoCoords | null;
  /** Radius in meters for location bias (default: 50km) */
  radiusMeters?: number;
  /** Place types to search for (default: ['gas_station']) */
  includedTypes?: string[];
}

export interface UseGoogleMapsAutocompleteReturn {
  isLoaded: boolean;
  createAutocompleteSession: () => Promise<any | null>;
  searchAddresses: (
    query: string,
    options?: AddressSearchOptions
  ) => Promise<AddressPrediction[]>;
  searchPlaces: (
    query: string,
    options?: PlaceSearchOptions
  ) => Promise<PlacePrediction[]>;
  fetchAddressDetails: (
    addressId: string,
    sessionToken?: any
  ) => Promise<AddressData | null>;
  fetchPlaceDetails: (
    placeId: string,
    sessionToken?: any
  ) => Promise<PlaceData | null>;
  reverseGeocode: (coords: GeoCoords) => Promise<AddressData | null>;
  geocodeAddress: (address: string) => Promise<AddressData | null>;
  parsePlaceAddress: (place: any) => AddressData;
}

// ============================================
// Constants
// ============================================

const DEFAULT_RADIUS_METERS = 50000; // 50km
const DEFAULT_PLACE_TYPES = ["establishment"];

// ============================================
// Hook
// ============================================

export const useGoogleMapsAutocomplete =
  (): UseGoogleMapsAutocompleteReturn => {
    const { loadLibraries } = useGoogleMapsContext();

    const placesLibraryRef = useRef<any>(null);
    // The load in flight — awaited by a call that comes before it finishes (a look-up the moment a map opens).
    const loadingLibraryRef = useRef<Promise<any> | null>(null);
    const [isPlacesLoaded, setIsPlacesLoaded] = useState(false);
    const loadLibrariesRef = useRef(loadLibraries);

    loadLibrariesRef.current = loadLibraries;

    /** The Places library: loaded once, shared by every caller; null when it cannot be loaded. */
    const ensurePlacesLibrary = useCallback(async (): Promise<any> => {
      if (placesLibraryRef.current) {
        return placesLibraryRef.current;
      }

      if (!loadingLibraryRef.current) {
        loadingLibraryRef.current = loadLibrariesRef.current(["places"])
          .then(({ places }) => {
            placesLibraryRef.current = places;
            setIsPlacesLoaded(true);

            return places;
          })
          .catch((error) => {
            console.error("Failed to load places library", error);

            return null;
          })
          .finally(() => {
            loadingLibraryRef.current = null;
          });
      }

      return loadingLibraryRef.current;
    }, []);

    // Eagerly load places library when hook initializes
    useEffect(() => {
      void ensurePlacesLibrary();
    }, [ensurePlacesLibrary]);

    // ⚠ Awaits a load in flight (it used to wait 100ms and give up — a cold page's first look-up always said "no
    //   address", 2026-10-23), and starts one when none is running (a load that failed can be tried again).
    const getPlacesLibrary = useCallback(async () => {
      const places = await ensurePlacesLibrary();

      if (!places) {
        console.error("Places library not loaded");
      }

      return places;
    }, [ensurePlacesLibrary]);

    const parsePlaceAddress = useCallback((place: any): AddressData => {
      const addressData: AddressData = {
        addressId: place.id,
        displayName: null,
        address1: null,
        address2: null,
        city: null,
        country: null,
        latitude: place.location?.lat() || null,
        longitude: place.location?.lng() || null,
        postalCode: null,
        region: null,
      };

      let streetNumber = "";
      let streetName = "";

      if (Array.isArray(place.addressComponents)) {
        place.addressComponents.forEach((component: any) => {
          const types = component.types || [];
          const longName = component.longText || "";
          const shortName = component.shortText || "";

          if (types.includes("street_number")) {
            streetNumber = longName;
          }

          if (types.includes("route")) {
            streetName = longName;
          }

          if (types.includes("subpremise")) {
            addressData.address2 = longName;
          }

          if (types.includes("locality")) {
            addressData.city = longName;
          }

          if (types.includes("administrative_area_level_1")) {
            addressData.region = shortName;
          }

          if (types.includes("postal_code")) {
            addressData.postalCode = longName;
          }

          if (types.includes("country")) {
            addressData.country = shortName;
          }
        });
      }

      addressData.displayName =
        place.displayName?.toString() || place.formattedAddress || "";
      addressData.address1 = [streetNumber, streetName]
        .filter((part: string) => part.trim() !== "")
        .join(" ");

      // A street address comes back typed street_address / premise / route; a
      // business or POI carries "establishment". Callers use this to decide
      // whether displayName is a NAME worth keeping or just the street line
      // repeated — see AddressData.isEstablishment.
      const placeTypes: string[] = Array.isArray(place.types) ? place.types : [];
      addressData.placeType = placeTypes[0] ?? null;
      addressData.isEstablishment =
        placeTypes.includes("establishment") || placeTypes.includes("point_of_interest");

      return addressData;
    }, []);

    const createAutocompleteSession = useCallback(async () => {
      const placesLibrary = await getPlacesLibrary();

      if (!placesLibrary) {
        console.error("Places library not available for session token");
        return null;
      }

      const { AutocompleteSessionToken } = placesLibrary;

      if (!AutocompleteSessionToken) {
        console.error("AutocompleteSessionToken not available");
        return null;
      }

      return new AutocompleteSessionToken();
    }, [getPlacesLibrary]);

    const searchAddresses = useCallback(
      async (
        query: string,
        options: AddressSearchOptions = {}
      ): Promise<AddressPrediction[]> => {
        const {
          sessionToken = null,
          locationBias = null,
          radiusMeters = DEFAULT_RADIUS_METERS,
        } = options;

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary || !placesLibrary.Place) {
          console.error("Places library not available for predictions");
          return [];
        }

        const { AutocompleteSuggestion } = placesLibrary;

        if (!AutocompleteSuggestion) {
          console.error(
            "AutocompleteSuggestion not available in places library"
          );
          return [];
        }

        try {
          const request: any = {
            input: query,
            includedPrimaryTypes: ["street_address", "premise"],
          };

          if (sessionToken) {
            request.sessionToken = sessionToken;
          }

          // Add location bias if coordinates are available
          if (locationBias?.latitude && locationBias?.longitude) {
            request.locationBias = {
              center: {
                lat: locationBias.latitude,
                lng: locationBias.longitude,
              },
              radius: radiusMeters,
            };
            request.origin = {
              lat: locationBias.latitude,
              lng: locationBias.longitude,
            }
          }

          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          if (!suggestions || suggestions.length === 0) {
            return [];
          }

          const predictions: AddressPrediction[] = suggestions.map(
            (suggestion: any) => {
              const prediction = suggestion.placePrediction;

              return {
                addressId: prediction.placeId,
                displayName: prediction.text?.toString() || "",
                types: prediction.types || [],
                distanceMeters: prediction.distanceMeters
              };
            }
          );

          return predictions.sort((a: any, b: any) => a.distanceMeters - b.distanceMeters);
        } catch (error) {
          console.error("Failed to fetch autocomplete suggestions:", error);
          return [];
        }
      },
      [getPlacesLibrary]
    );

    /**
     * Search for places/establishments (e.g., gas stations)
     * Supports location bias for relevant nearby results
     */
    const searchPlaces = useCallback(
      async (
        query: string,
        options: PlaceSearchOptions = {}
      ): Promise<PlacePrediction[]> => {
        const {
          sessionToken = null,
          locationBias = null,
          radiusMeters = DEFAULT_RADIUS_METERS,
          includedTypes = DEFAULT_PLACE_TYPES,
        } = options;

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for place search");
          return [];
        }

        const { AutocompleteSuggestion } = placesLibrary;

        if (!AutocompleteSuggestion) {
          console.error(
            "AutocompleteSuggestion not available in places library"
          );
          return [];
        }

        try {
          const request: any = {
            input: query,
            includedPrimaryTypes: includedTypes,
          };

          if (sessionToken) {
            request.sessionToken = sessionToken;
          }

          // Add location bias if coordinates are available
          if (locationBias?.latitude && locationBias?.longitude) {
            request.locationBias = {
              center: {
                lat: locationBias.latitude,
                lng: locationBias.longitude,
              },
              radius: radiusMeters,
            };
            request.origin = {
              lat: locationBias.latitude,
              lng: locationBias.longitude,
            }
          }

          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          if (!suggestions || suggestions.length === 0) {
            return [];
          }

          const predictions: PlacePrediction[] = suggestions.map(
            (suggestion: any) => {
              const prediction = suggestion.placePrediction;

              return {
                placeId: prediction.placeId,
                name: prediction.mainText?.toString() || "",
                address: prediction.secondaryText?.toString() || "",
                types: prediction.types || [],
                distanceMeters: prediction.distanceMeters
              };
            }
          );

          return predictions.sort((a: any, b: any) => a.distanceMeters - b.distanceMeters);
        } catch (error) {
          console.error("Failed to fetch place suggestions:", error);
          return [];
        }
      },
      [getPlacesLibrary]
    );

    const fetchAddressDetails = useCallback(
      async (
        addressId: string,
        sessionToken: any = null
      ): Promise<AddressData | null> => {
        if (!addressId) {
          console.error("No addressId provided to fetchAddressDetails");
          return null;
        }

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for place details");
          return null;
        }

        try {
          const { Place } = placesLibrary;

          if (!Place) {
            console.error("Place class not available in places library");
            return null;
          }

          const place = new Place({ id: addressId });

          const fetchOptions: any = {
            fields: [
              "addressComponents",
              "formattedAddress",
              "location",
              "displayName",
              // Tells an establishment apart from a plain street address, so a
              // caller storing one line knows whether displayName is a real name.
              "types",
            ],
          };

          if (sessionToken) {
            fetchOptions.sessionToken = sessionToken;
          }

          await place.fetchFields(fetchOptions);

          const addressData = parsePlaceAddress(place);

          return addressData;
        } catch (error) {
          console.error("Failed to fetch address details:", error);
          return null;
        }
      },
      [getPlacesLibrary, parsePlaceAddress]
    );

    /**
     * Fetch full details for a place (establishment)
     * Returns place name and full address data
     */
    const fetchPlaceDetails = useCallback(
      async (
        placeId: string,
        sessionToken: any = null
      ): Promise<PlaceData | null> => {
        if (!placeId) {
          console.error("No placeId provided to fetchPlaceDetails");
          return null;
        }

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for place details");
          return null;
        }

        try {
          const { Place } = placesLibrary;

          if (!Place) {
            console.error("Place class not available in places library");
            return null;
          }

          const place = new Place({ id: placeId });

          const fetchOptions: any = {
            fields: [
              "id",
              "displayName",
              "types",
              "addressComponents",
              "formattedAddress",
              "location",
              "websiteURI",
              "nationalPhoneNumber",
              "internationalPhoneNumber",
              "googleMapsURI",
            ],
          };

          if (sessionToken) {
            fetchOptions.sessionToken = sessionToken;
          }

          await place.fetchFields(fetchOptions);

          const addressData = parsePlaceAddress(place);
          const placeName = place.displayName?.toString() || "";

          return {
            placeId: place.id,
            name: placeName,
            address: addressData,
            website: place.websiteURI ?? null,
            phone: place.nationalPhoneNumber ?? place.internationalPhoneNumber ?? null,
            googleMapsUri: place.googleMapsURI ?? null,
          };
        } catch (error) {
          console.error("Failed to fetch place details:", error);
          return null;
        }
      },
      [getPlacesLibrary, parsePlaceAddress]
    );

    /**
     * Reverse geocode coordinates to get address
     * Uses Google Geocoding API
     */
    const reverseGeocode = useCallback(
      async (coords: GeoCoords): Promise<AddressData | null> => {
        if (!isValidCoordinate(coords)) {
          console.error("Invalid coordinates provided to reverseGeocode");
          return null;
        }

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for reverse geocoding");
          return null;
        }

        try {
          // Use the Geocoder API for reverse geocoding
          const geocoder = new google.maps.Geocoder();

          const response = await geocoder.geocode({
            location: {
              lat: coords.latitude,
              lng: coords.longitude,
            },
          });

          if (!response.results || response.results.length === 0) {
            console.error("No results from reverse geocoding");
            return null;
          }

          // Get the most specific result (usually the first one)
          const result = response.results[0];

          // Parse the geocoder result into AddressData format
          const addressData: AddressData = {
            addressId: result.place_id,
            displayName: result.formatted_address || null,
            address1: null,
            address2: null,
            city: null,
            country: null,
            latitude: coords.latitude,
            longitude: coords.longitude,
            postalCode: null,
            region: null,
          };

          let streetNumber = "";
          let streetName = "";

          if (Array.isArray(result.address_components)) {
            result.address_components.forEach((component) => {
              const types = component.types || [];
              const longName = component.long_name || "";
              const shortName = component.short_name || "";

              if (types.includes("street_number")) {
                streetNumber = longName;
              }

              if (types.includes("route")) {
                streetName = longName;
              }

              if (types.includes("subpremise")) {
                addressData.address2 = longName;
              }

              if (types.includes("locality")) {
                addressData.city = longName;
              }

              if (types.includes("administrative_area_level_1")) {
                addressData.region = shortName;
              }

              if (types.includes("postal_code")) {
                addressData.postalCode = longName;
              }

              if (types.includes("country")) {
                addressData.country = shortName;
              }
            });
          }

          addressData.address1 = [streetNumber, streetName]
            .filter((part) => part.trim() !== "")
            .join(" ");

          return addressData;
        } catch (error) {
          console.error("Failed to reverse geocode:", error);
          return null;
        }
      },
      [getPlacesLibrary]
    );

    /**
     * Forward geocode an address string to get coordinates and structured address data.
     * Uses Google Geocoding API.
     */
    const geocodeAddress = useCallback(
      async (address: string): Promise<AddressData | null> => {
        if (!address || !address.trim()) {
          console.error("No address provided to geocodeAddress");
          return null;
        }

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for forward geocoding");
          return null;
        }

        try {
          const geocoder = new google.maps.Geocoder();

          const response = await geocoder.geocode({
            address: address.trim(),
          });

          if (!response.results || response.results.length === 0) {
            console.error("No results from forward geocoding for:", address);
            return null;
          }

          const result = response.results[0];
          const location = result.geometry?.location;

          const addressData: AddressData = {
            addressId: result.place_id,
            displayName: result.formatted_address || null,
            address1: null,
            address2: null,
            city: null,
            country: null,
            latitude: location?.lat() ?? null,
            longitude: location?.lng() ?? null,
            postalCode: null,
            region: null,
          };

          let streetNumber = "";
          let streetName = "";

          if (Array.isArray(result.address_components)) {
            result.address_components.forEach((component) => {
              const types = component.types || [];
              const longName = component.long_name || "";
              const shortName = component.short_name || "";

              if (types.includes("street_number")) {
                streetNumber = longName;
              }
              if (types.includes("route")) {
                streetName = longName;
              }
              if (types.includes("subpremise")) {
                addressData.address2 = longName;
              }
              if (types.includes("locality")) {
                addressData.city = longName;
              }
              if (types.includes("administrative_area_level_1")) {
                addressData.region = shortName;
              }
              if (types.includes("postal_code")) {
                addressData.postalCode = longName;
              }
              if (types.includes("country")) {
                addressData.country = shortName;
              }
            });
          }

          addressData.address1 = [streetNumber, streetName]
            .filter((part) => part.trim() !== "")
            .join(" ");

          return addressData;
        } catch (error) {
          console.error("Failed to forward geocode address:", error);
          return null;
        }
      },
      [getPlacesLibrary]
    );

    return {
      isLoaded: isPlacesLoaded,
      createAutocompleteSession,
      searchAddresses,
      searchPlaces,
      fetchAddressDetails,
      fetchPlaceDetails,
      reverseGeocode,
      geocodeAddress,
      parsePlaceAddress,
    };
  };
